import api from "./api";
import { readIsTeacher } from "utils/teacherFlags";

export const TEACHER_EDITABLE_FIELDS = [
  "regNumber",
  "name",
  "preName",
  "gender",
  "degree",
  "phone",
  "whatsapp",
  "email",
  "regDate",
  "status",
  "certificate",
  "identificationNumber",
  "dob",
  "integrationOrderNumber",
  "recruiterId",
  "isTeacher",
];

const BOOLEAN_FIELDS = new Set(["isTeacher"]);
const NUMBER_FIELDS = new Set(["recruiterId"]);
const DATE_FIELDS = new Set(["regDate", "dob"]);

const FIELD_ALIASES = {
  regNumber: ["regNumber", "RegNumber"],
  name: ["name", "Name"],
  preName: ["preName", "PreName"],
  gender: ["gender", "Gender"],
  degree: ["degree", "Degree"],
  phone: ["phone", "Phone"],
  whatsapp: ["whatsapp", "Whatsapp", "WhatsApp"],
  email: ["email", "Email"],
  regDate: ["regDate", "RegDate"],
  status: ["status", "Status"],
  certificate: ["certificate", "Certificate"],
  identificationNumber: ["identificationNumber", "IdentificationNumber"],
  dob: ["dob", "DOB", "Dob"],
  integrationOrderNumber: ["integrationOrderNumber", "IntegrationOrderNumber"],
  recruiterId: ["recruiterId", "RecruiterId"],
  isTeacher: ["isTeacher", "IsTeacher"],
};

function firstValue(object, keys) {
  for (const key of keys) {
    if (object?.[key] !== null && object?.[key] !== undefined) return object[key];
  }
  return "";
}

function dateInputValue(value) {
  const raw = String(value ?? "").trim();
  return raw ? raw.slice(0, 10) : "";
}

export function teacherEditValues(profile = {}) {
  return Object.fromEntries(TEACHER_EDITABLE_FIELDS.map((field) => {
    const raw = firstValue(profile, FIELD_ALIASES[field]);
    if (BOOLEAN_FIELDS.has(field)) return [field, readIsTeacher(raw)];
    if (DATE_FIELDS.has(field)) return [field, dateInputValue(raw)];
    return [field, raw == null ? "" : String(raw)];
  }));
}

export function normalizeTeacherEditValue(field, value) {
  if (BOOLEAN_FIELDS.has(field)) return value === true || String(value).toLowerCase() === "true";
  if (NUMBER_FIELDS.has(field)) {
    const trimmed = String(value ?? "").trim();
    return trimmed === "" ? null : Number(trimmed);
  }
  if (DATE_FIELDS.has(field)) {
    const trimmed = String(value ?? "").trim();
    return trimmed || null;
  }
  return String(value ?? "").trim();
}

/** Builds an allow-listed, partial payload. Protected profile fields can never pass through. */
export function buildTeacherPatch(initialValues, currentValues) {
  const patch = {};
  TEACHER_EDITABLE_FIELDS.forEach((field) => {
    const initial = normalizeTeacherEditValue(field, initialValues?.[field]);
    const current = normalizeTeacherEditValue(field, currentValues?.[field]);
    if (!Object.is(initial, current)) patch[field] = current;
  });
  return patch;
}

const normalizedFieldName = (key) => String(key ?? "").replace(/[^a-z0-9]/gi, "").toLowerCase();
const ERROR_FIELD_MAP = Object.fromEntries([
  ...TEACHER_EDITABLE_FIELDS.map((field) => [normalizedFieldName(field), field]),
  ["matricule", "regNumber"],
].map(([key, field]) => [key, field]));

export function teacherUpdateFieldErrors(error) {
  const source = error?.response?.data?.errors ?? error?.response?.data?.Errors;
  if (!source || typeof source !== "object" || Array.isArray(source)) return {};

  return Object.entries(source).reduce((result, [key, messages]) => {
    const field = ERROR_FIELD_MAP[normalizedFieldName(key)];
    if (!field) return result;
    const message = Array.isArray(messages) ? messages.find(Boolean) : messages;
    if (message) result[field] = String(message);
    return result;
  }, {});
}

function authHeaders() {
  const token = localStorage.getItem("tk446655440000");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Content-Type": "application/json",
  };
}

export async function updateTeacher(teacherId, changes, { mobilePanel = false } = {}) {
  const endpoint = mobilePanel
    ? `/mobile/panel/teachers/${encodeURIComponent(teacherId)}`
    : `/Teacher/${encodeURIComponent(teacherId)}`;
  const { data } = await api.patch(endpoint, changes, {
    headers: authHeaders(),
    skipGlobalForbidden: true,
  });
  return data;
}

export async function fetchTeacherRecruiterOptions() {
  const { data } = await api.get("/Recruiters/list", { headers: authHeaders() });
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.items)
        ? data.items
        : [];
  return rows.map((item) => ({
    id: item.id ?? item.Id,
    text: item.name ?? item.Name ?? item.userName ?? item.UserName ?? String(item.id ?? item.Id),
  }));
}
